"use client";
import { normalizeClientRouteFamily } from "@/utils/client-route-family";
import type { AtlasSentryErrorSource } from "@/utils/sentry-event-normalization";
import * as Sentry from "@sentry/nextjs";
import type { Scope } from "@sentry/nextjs";

type ClientAttributionValue = string | number | boolean;

export type ClientSentryWorkspaceType = "agency" | "organization";

export type ClientSentryAttribution = {
  actorId?: string | null;
  agencyId?: string | null;
  agencyMembershipId?: string | null;
  organizationId?: string | null;
  organizationMembershipId?: string | null;
  workspaceId?: string | null;
  workspaceSlug?: string | null;
  workspaceType?: ClientSentryWorkspaceType | null;
  route?: string | null;
  routeFamily?: string | null;
  clientHost?: string | null;
  clientPathname?: string | null;
  clientUrl?: string | null;
  errorDigest?: string | null;
  errorSource?: AtlasSentryErrorSource | null;
};

type ClientLocationLike = Pick<Location, "hostname" | "origin" | "pathname">;

const workspaceHostSuffixes = [
  ".iebridge.com",
  ".iebridge.app",
  ".iebridge.test",
] as const;
const reservedWorkspaceHosts = new Set(["app", "iebridge", "localhost", "www"]);
const knownClientSentryTagKeys = [
  "atlas.actor_id",
  "atlas.agency_id",
  "atlas.agency_membership_id",
  "atlas.error_source",
  "atlas.organization_id",
  "atlas.organization_membership_id",
  "atlas.route",
  "atlas.route_family",
  "atlas.workspace_id",
  "atlas.workspace_slug",
  "atlas.workspace_type",
] as const;

let currentClientAttribution: Record<string, ClientAttributionValue> = {};
const clientIdentityAttributionKeys = [
  "actorId",
  "agencyId",
  "agencyMembershipId",
  "organizationId",
  "organizationMembershipId",
  "workspaceId",
  "workspaceSlug",
  "workspaceType",
] as const;

function isClientAttributionValue(value: unknown): value is ClientAttributionValue {
  return typeof value === "string" || typeof value === "number" || typeof value === "boolean";
}

function getBrowserLocation(): ClientLocationLike | null {
  if (typeof window === "undefined") {
    return null;
  }

  return window.location;
}

export function inferWorkspaceSlugFromHostname(hostname: string | null | undefined): string | null {
  if (!hostname) {
    return null;
  }

  for (const suffix of workspaceHostSuffixes) {
    if (!hostname.endsWith(suffix)) {
      continue;
    }

    const subdomain = hostname.slice(0, -suffix.length).split(".").at(-1);
    if (!subdomain || reservedWorkspaceHosts.has(subdomain)) {
      return null;
    }

    return subdomain;
  }

  return null;
}

function isAccountNeutralHost(hostname: string | null | undefined): boolean {
  if (!hostname || hostname === "localhost") {
    return true;
  }

  return workspaceHostSuffixes.some((suffix) => {
    if (!hostname.endsWith(suffix)) {
      return false;
    }

    const subdomain = hostname.slice(0, -suffix.length).split(".").at(-1);
    return !subdomain || reservedWorkspaceHosts.has(subdomain);
  });
}

export function sanitizeClientSentryAttribution(
  attribution: Partial<ClientSentryAttribution>,
): Record<string, ClientAttributionValue> {
  const safeAttribution: Record<string, ClientAttributionValue> = {};

  for (const [key, value] of Object.entries(attribution)) {
    if (isClientAttributionValue(value) && value !== "") {
      safeAttribution[key] = value;
    }
  }

  return safeAttribution;
}

export function buildClientSentryAttribution(
  attribution: Partial<ClientSentryAttribution>,
  location: ClientLocationLike | null = getBrowserLocation(),
): Record<string, ClientAttributionValue> {
  const inferredWorkspaceSlug = inferWorkspaceSlugFromHostname(location?.hostname);
  const clientPathname = location?.pathname;
  const route = attribution.route ?? clientPathname;
  const routeFamily = route
    ? normalizeClientRouteFamily(route)
    : attribution.routeFamily;

  return sanitizeClientSentryAttribution({
    ...(location
      ? {
        clientHost: location.hostname,
        clientPathname,
        clientUrl: `${location.origin}${clientPathname}`,
        route: clientPathname,
      }
      : {}),
    ...(inferredWorkspaceSlug ? { workspaceSlug: inferredWorkspaceSlug } : {}),
    ...attribution,
    ...(route ? { route } : {}),
    ...(routeFamily ? { routeFamily } : {}),
  });
}

function getClientSentryTagKey(key: string): string | null {
  switch (key) {
    case "actorId": {
      return "atlas.actor_id";
    }
    case "agencyId": {
      return "atlas.agency_id";
    }
    case "agencyMembershipId": {
      return "atlas.agency_membership_id";
    }
    case "organizationId": {
      return "atlas.organization_id";
    }
    case "organizationMembershipId": {
      return "atlas.organization_membership_id";
    }
    case "workspaceId": {
      return "atlas.workspace_id";
    }
    case "workspaceSlug": {
      return "atlas.workspace_slug";
    }
    case "workspaceType": {
      return "atlas.workspace_type";
    }
    case "route": {
      return "atlas.route";
    }
    case "routeFamily": {
      return "atlas.route_family";
    }
    case "errorSource": {
      return "atlas.error_source";
    }
    default: {
      return null;
    }
  }
}

function buildSentryTags(
  safeAttribution: Record<string, ClientAttributionValue>,
): Record<string, ClientAttributionValue | undefined> {
  const sentryTags: Record<string, ClientAttributionValue | undefined> = Object.fromEntries(
    knownClientSentryTagKeys.map(tagKey => [tagKey, undefined]),
  );

  for (const [key, value] of Object.entries(safeAttribution)) {
    const tagKey = getClientSentryTagKey(key);
    if (tagKey) {
      sentryTags[tagKey] = value;
    }
  }

  return sentryTags;
}

function mergeClientSentryAttribution(
  attribution: Partial<ClientSentryAttribution>,
) {
  const nextAttribution = buildClientSentryAttribution(attribution);
  const mergedAttribution = {
    ...currentClientAttribution,
    ...nextAttribution,
  };

  if (nextAttribution.workspaceType === "agency") {
    delete mergedAttribution.organizationId;
    delete mergedAttribution.organizationMembershipId;
  }
  if (nextAttribution.workspaceType === "organization") {
    delete mergedAttribution.agencyId;
    delete mergedAttribution.agencyMembershipId;
  }
  if (
    typeof nextAttribution.clientHost === "string"
    && isAccountNeutralHost(nextAttribution.clientHost)
    && !nextAttribution.actorId
    && !nextAttribution.workspaceType
  ) {
    for (const key of clientIdentityAttributionKeys) {
      delete mergedAttribution[key];
    }
  }

  currentClientAttribution = {
    ...mergedAttribution,
  };

  return currentClientAttribution;
}

export function applyClientSentryAttribution(
  attribution: Partial<ClientSentryAttribution> = {},
) {
  const safeAttribution = mergeClientSentryAttribution(attribution);

  Sentry.setContext("atlas.client", safeAttribution);
  Sentry.setTags(buildSentryTags(safeAttribution));

  if (typeof safeAttribution.routeFamily === "string") {
    Sentry.getIsolationScope().setTransactionName(safeAttribution.routeFamily);
  }

  if (typeof safeAttribution.actorId === "string") {
    Sentry.setUser({
      id: safeAttribution.actorId,
    });
  } else {
    Sentry.setUser(null);
  }

  return safeAttribution;
}

export function applyClientSentryAttributionToScope(
  scope: Scope,
  attribution: Partial<ClientSentryAttribution> = {},
) {
  const safeAttribution = mergeClientSentryAttribution(attribution);

  scope.setContext("atlas.client", safeAttribution);
  scope.setTags(buildSentryTags(safeAttribution));

  if (typeof safeAttribution.routeFamily === "string") {
    scope.setTransactionName(safeAttribution.routeFamily);
  }

  if (typeof safeAttribution.actorId === "string") {
    scope.setUser({
      id: safeAttribution.actorId,
    });
  } else {
    scope.setUser(null);
  }

  return safeAttribution;
}

export function captureClientGlobalError(
  error: Error,
  attribution: Partial<ClientSentryAttribution> = {},
) {
  const safeAttribution = applyClientSentryAttribution(attribution);

  Sentry.captureException(error, {
    contexts: {
      "atlas.client": safeAttribution,
    },
  });
}
